import { useQuery } from '@apollo/client';

import user from 'js/lib/user';

import EnterpriseExperiments from 'bundles/epic/clients/Enterprise';
import { useNaptime } from 'bundles/naptimejs';
import EnterpriseSelfServeContractProductPrices from 'bundles/naptimejs/resources/enterpriseSelfServeContractProductPrices.v1';
import EnterpriseSelfServeProductPrice from 'bundles/wes-common/queries/EnterpriseSelfServeProductPrice.graphql';
import type {
  EnterpriseSelfServeProductPriceQuery,
  EnterpriseSelfServeProductPriceQueryVariables,
} from 'bundles/wes-common/queries/__generated__/EnterpriseSelfServeProductPrice';
import type { ProductPrice } from 'bundles/wes-common/types';

type UseProductPriceParams = {
  learnerCount?: number;
  billingType?: number;
  enterprisePromotionId?: string;
  skipApiCall?: boolean;
};

type UseProductPriceReturn = {
  productPrice?: Array<ProductPrice>;
  defaultProductPrice?: Array<ProductPrice>;
  loading: boolean;
  error: unknown;
};

export const useEnterpriseSelfServePrice = ({
  learnerCount,
  billingType,
  enterprisePromotionId,
  skipApiCall,
}: UseProductPriceParams): UseProductPriceReturn => {
  const userId = user.get()?.id ?? 0;

  const useGraphqlQuery = EnterpriseExperiments.get('enableSelfServePriceFromGraphQL');
  // GraphQL Query approach - skip when not needed
  const {
    loading: gqlLoading,
    error: gqlError,
    data: graphqlData,
  } = useQuery<EnterpriseSelfServeProductPriceQuery, EnterpriseSelfServeProductPriceQueryVariables>(
    EnterpriseSelfServeProductPrice,
    {
      variables: {
        userLimit: learnerCount ?? 1,
        termInstallmentsTotal: billingType ?? 1,
        enterprisePromotionId,
        countryIsoCode: '',
        currencyCode: 'USD',
        userId: userId.toString(),
      },
      context: { clientName: 'gatewayGql' },
      skip: !useGraphqlQuery || skipApiCall,
    }
  );

  // Naptime approach - skip when not needed
  const { data } = useNaptime<{ productPrice?: Array<ProductPrice>; defaultProductPrice?: Array<ProductPrice> }>(() => {
    if (useGraphqlQuery || skipApiCall) {
      // Don't fetch when using GraphQL
      return { productPrice: undefined, defaultProductPrice: undefined };
    }

    return {
      ...(learnerCount && billingType
        ? {
            productPrice: EnterpriseSelfServeContractProductPrices.getEnterpriseSelfServeContractProductPrice({
              userLimit: learnerCount,
              userIdOpt: userId,
              billingType,
              termInstallmentsTotal: billingType,
              enterprisePromotionId,
            }),
          }
        : { productPrice: undefined }),
      defaultProductPrice: EnterpriseSelfServeContractProductPrices.getEnterpriseSelfServeContractProductPrice({
        userLimit: 1,
        billingType: 1,
        termInstallmentsTotal: 1,
        enterprisePromotionId,
      }),
    };
  }, [learnerCount, billingType, skipApiCall, enterprisePromotionId, userId, useGraphqlQuery]);

  // Transform GraphQL data to match ProductPrice structure
  const transformProductPriceData = (data: typeof graphqlData) =>
    data?.EnterpriseContractPriceQueries?.getEnterpriseSelfServeContractProductPrice?.elements?.map(
      (element): ProductPrice => ({
        unitPrice: element.price.unitPrice.toString(),
        rawLocalAmountTotal: element.price.rawLocalAmountTotal.toString(),
        discountPercent: element.price.discountPercent,
        countryIsoCode: element.price.countryIsoCode,
        actualLocalAmountDueToday: element.price.actualLocalAmountDueToday.toString(),
        actualLocalAmountTotal: element.price.actualLocalAmountTotal.toString(),
        rawLocalAmountDueToday: element.price.rawLocalAmountDueToday.toString(),
        actualLocalDiscountedAmount: element.price.actualLocalDiscountedAmount.toString(),
        rawAmountTotal: element.price.rawAmountTotal.toString(),
        rawAmountDueToday: element.price.rawAmountDueToday.toString(),
        currencyCode: element.price.currencyCode,
        // Add missing fields to match ProductPrice interface
        fxRateId: 0, // Default value as number
        id: `${element.id.userLimit}-${element.id.billingType}`, // Construct ID
      })
    );

  const transformedGraphQLData = transformProductPriceData(graphqlData);

  // Safely extract Naptime data
  const naptimeProductPrice = data?.productPrice;
  const naptimeDefaultProductPrice = data?.defaultProductPrice;

  // Return unified interface
  return {
    productPrice: useGraphqlQuery ? transformedGraphQLData : naptimeProductPrice,
    defaultProductPrice: useGraphqlQuery ? transformedGraphQLData : naptimeDefaultProductPrice,
    loading: useGraphqlQuery ? gqlLoading : false,
    error: useGraphqlQuery ? gqlError : null,
  };
};
